import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Lock, ArrowRight, Sparkles, Award, PartyPopper, PlayCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../App';
import { toast } from 'sonner';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        const [weeksRes, progressRes] = await Promise.all([
          api.get('/weeks'),
          api.get(`/users/${user.id}/progress`)
        ]);
        setWeeks(weeksRes.data);
        setProgress(progressRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load your progress');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const getWeekStatus = (weekNumber) => {
    if (!user) return 'locked';
    
    // Week 1 is always available immediately
    if (weekNumber === 1) {
      if (progress?.weeks_completed.includes(weekNumber)) {
        return 'completed';
      }
      if (progress?.weeks_in_progress?.includes(weekNumber)) {
        return 'in_progress';
      }
      return 'available';
    }
    
    // For other weeks, check time-based unlocking
    if (!user.created_at) return 'locked';
    
    // Calculate days since signup
    const signupDate = new Date(user.created_at);
    const today = new Date();
    const daysSinceSignup = Math.floor((today - signupDate) / (1000 * 60 * 60 * 24));
    
    // Week unlocks after (weekNumber - 1) * 7 days
    const daysNeededToUnlock = (weekNumber - 1) * 7;
    
    // Check if week is unlocked based on time
    if (daysSinceSignup >= daysNeededToUnlock) {
      // Check if completed
      if (progress?.weeks_completed.includes(weekNumber)) {
        return 'completed';
      }
      // Check if in progress
      if (progress?.weeks_in_progress?.includes(weekNumber)) {
        return 'in_progress';
      }
      return 'available';
    }
    
    return 'locked';
  };

  const getUnlockDate = (weekNumber) => {
    if (!user || !user.created_at) return null;
    
    const signupDate = new Date(user.created_at);
    const daysToAdd = (weekNumber - 1) * 7;
    const unlockDate = new Date(signupDate);
    unlockDate.setDate(unlockDate.getDate() + daysToAdd);
    
    return unlockDate.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const hasInsight = (weekNumber) => {
    return progress?.insights_generated.includes(weekNumber);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your journey...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex items-center justify-between">
            <div>
              <img 
                src="/kodo-logo.png" 
                alt="Kodo" 
                className="h-14"
              />
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">
                {progress && progress.total_responses > 0 ? 'Welcome back,' : 'Welcome,'}
              </p>
              <p className="font-semibold text-slate-900">{user?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        {/* Celebration Banner */}
        {progress && progress.weeks_completed.length === 6 && (
          <div 
            className="mb-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white cursor-pointer hover:shadow-2xl transition-shadow duration-300 animate-fade-in"
            onClick={() => navigate('/completion')}
            data-testid="completion-banner"
          >
            <div className="flex items-center gap-6">
              <div className="bg-white/20 p-4 rounded-full">
                <Award className="w-12 h-12" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-3xl font-bold mb-2 flex items-center gap-2">
                  Congratulations! <PartyPopper className="w-8 h-8" />
                </h3>
                <p className="text-blue-100 text-lg">
                  You've completed all 6 weeks! Click here to view your personalised summary and next steps.
                </p>
              </div>
              <ArrowRight className="w-8 h-8" />
            </div>
          </div>
        )}

        {/* Progress Overview */}
        <div className="mb-12 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
              Your Journey Progress
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-4xl font-bold text-primary mb-2">
                  {progress?.weeks_completed.length || 0}/6
                </div>
                <div className="text-sm font-medium text-slate-600">
                  Weeks Completed
                </div>
              </div>
              <div className="text-center p-6 bg-orange-50 rounded-xl">
                <div className="text-4xl font-bold text-secondary mb-2">
                  {progress?.total_responses || 0}
                </div>
                <div className="text-sm font-medium text-slate-600">
                  Activities Done
                </div>
              </div>
              <div className="text-center p-6 bg-cyan-50 rounded-xl">
                <div className="text-4xl font-bold text-accent-cyan mb-2">
                  {progress?.tasks_completed || 0}
                </div>
                <div className="text-sm font-medium text-slate-600">
                  Tasks Done
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Week Cards */}
        <div className="space-y-6">
          <h2 className="font-heading text-3xl font-bold text-slate-900 mb-8">
            6-Week Programme
          </h2>
          
          {weeks.map((week) => {
            const status = getWeekStatus(week.week_number);
            const hasGeneratedInsight = hasInsight(week.week_number);
            const isLocked = status === 'locked';
            const isCompleted = status === 'completed';
            const isInProgress = status === 'in_progress';
            
            // Calculate activities per week based on user's commitment
            const activitiesPerWeek = user.weekly_commitment === 60 ? 1 : user.weekly_commitment === 120 ? 2 : 3;
            const displayActivitiesCount = Math.min(week.activities.length, activitiesPerWeek);
            
            // Determine button text
            const getButtonText = () => {
              if (isCompleted) return 'Review';
              if (isInProgress) return 'Continue';
              return 'Start Week';
            };

            return (
              <div
                key={week.week_number}
                data-testid={`week-${week.week_number}-card`}
                className={`bg-white rounded-2xl p-8 border shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] transition-all duration-300 ${
                  isLocked ? 'opacity-60' : 'week-card hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)]'
                }`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {isCompleted ? (
                        <CheckCircle2 className="w-8 h-8 text-primary" data-testid={`week-${week.week_number}-completed-icon`} />
                      ) : isInProgress ? (
                        <PlayCircle className="w-8 h-8 text-amber-500" data-testid={`week-${week.week_number}-in-progress-icon`} />
                      ) : isLocked ? (
                        <Lock className="w-8 h-8 text-slate-400" data-testid={`week-${week.week_number}-locked-icon`} />
                      ) : (
                        <Circle className="w-8 h-8 text-slate-400" data-testid={`week-${week.week_number}-available-icon`} />
                      )}
                      <div>
                        <div className="text-sm font-medium text-primary">
                          Week {week.week_number}
                        </div>
                        <h3 className="font-heading text-2xl font-bold text-slate-900">
                          {week.title}
                        </h3>
                      </div>
                    </div>
                    
                    <p className="text-slate-600 leading-relaxed mb-4">
                      {week.description}
                    </p>

                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      {(() => {
                        const completedCount = progress?.responses_per_week?.[week.week_number] || 0;
                        if (isCompleted) {
                          return <span className="text-primary font-medium">{activitiesPerWeek}/{activitiesPerWeek} completed</span>;
                        } else if (isInProgress) {
                          return <span className="text-amber-600 font-medium">{completedCount}/{activitiesPerWeek} completed</span>;
                        } else {
                          return <span>{activitiesPerWeek} {activitiesPerWeek === 1 ? 'activity' : 'activities'}</span>;
                        }
                      })()}
                    </div>
                    
                    {isLocked && (
                      <div className="mt-3 text-sm text-slate-500">
                        Unlocks on {getUnlockDate(week.week_number)}
                      </div>
                    )}
                  </div>

                  <div>
                    {!isLocked && (
                      <Button
                        onClick={() => {
                          // Calculate the next incomplete activity (1-based for URL)
                          const completedCount = progress?.responses_per_week?.[week.week_number] || 0;
                          const startActivity = isInProgress ? completedCount + 1 : 1;
                          navigate(`/week/${week.week_number}?activity=${startActivity}`);
                        }}
                        data-testid={`week-${week.week_number}-start-button`}
                        className="h-12 px-8 rounded-full bg-primary text-white font-semibold shadow-lg hover:shadow-xl btn-hover"
                      >
                        {getButtonText()}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    )}
                    {isLocked && (
                      <Button
                        disabled
                        className="h-12 px-8 rounded-full bg-slate-200 text-slate-400 font-semibold cursor-not-allowed"
                        data-testid={`week-${week.week_number}-locked-button`}
                      >
                        Locked
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Motivational Message */}
        {progress && progress.weeks_completed.length > 0 && progress.weeks_completed.length < 6 && (
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
            <h3 className="font-heading text-2xl font-bold mb-3">
              You're Making Progress!
            </h3>
            <p className="text-blue-100 text-lg">
              Keep going - every week brings you closer to clarity about your next career move.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="py-8 bg-slate-50 border-t border-slate-100 mt-12">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/kodo-logo.png" 
                alt="Kodo" 
                className="h-8 w-auto"
              />
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <button 
                onClick={() => navigate('/about')}
                className="hover:text-primary transition-colors"
              >
                About Us
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="hover:text-primary transition-colors"
              >
                Terms & Conditions
              </button>
              <a 
                href="mailto:hello@getkodo.co.uk"
                className="hover:text-primary transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
          {/* Company registration */}
          <div className="mt-6 pt-4 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} Colourful Clay Limited. Company No. 11799231
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;